/**
 * useKeyboardShortcuts — V14.62 Sprint 19 (Lab).
 *
 * Raccourcis pro :
 *   V → Sélection
 *   D → Dessiner un mur
 *   W → Fenêtre
 *   P → Porte
 *   S → Escalier / trémie
 *   T → Tracer le terrain / limite de propriété (polygone)
 *   E → Gomme / Supprimer
 *   C → Calibration (Règle)
 *   Tab → (en mode Dessiner un Mur) cycle entre Porteur / Cloison fine / Cloison basse
 *   Esc → Annule l'opération en cours (drawing, calibration, terrain)
 *   Enter → Ferme le polygone terrain en cours
 *   Ctrl+Z       → Undo
 *   Ctrl+Shift+Z → Redo
 *   Ctrl+Y       → Redo (alt)
 *
 * Désactive automatiquement les raccourcis lorsqu'un champ texte / input
 * a le focus (sinon ils interceptent la saisie utilisateur).
 */
import { useEffect } from 'react';
import { useGeometryStore, TOOLS, WALL_KIND_DEFS } from './useGeometryStore';

const TOOL_KEYS = {
  v: TOOLS.SELECT,
  d: TOOLS.DRAW_WALL,
  w: TOOLS.PLACE_WINDOW,
  p: TOOLS.PLACE_DOOR,
  s: TOOLS.PLACE_STAIRS,
  t: TOOLS.DRAW_TERRAIN,
  e: TOOLS.DELETE,
  c: TOOLS.CALIBRATE,
};

const isEditableTarget = (el) => {
  if (!el) return false;
  const tag = (el.tagName || '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
};

const useKeyboardShortcuts = ({ enabled = true } = {}) => {
  const setTool = useGeometryStore((s) => s.setTool);
  const setDrawingFrom = useGeometryStore((s) => s.setDrawingFrom);
  const setChainStart = useGeometryStore((s) => s.setChainStart);
  const setCalibrationFrom = useGeometryStore((s) => s.setCalibrationFrom);
  const setStairsFrom = useGeometryStore((s) => s.setStairsFrom);
  const cancelTerrainDraft = useGeometryStore((s) => s.cancelTerrainDraft);
  const commitTerrain = useGeometryStore((s) => s.commitTerrain);
  const setDrawWallKind = useGeometryStore((s) => s.setDrawWallKind);
  const clearSelection = useGeometryStore((s) => s.clearSelection);

  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      if (isEditableTarget(e.target)) return;
      const key = e.key;

      // Undo / Redo
      if ((e.ctrlKey || e.metaKey) && !e.altKey) {
        const k = key.toLowerCase();
        if (k === 'z' && !e.shiftKey) {
          e.preventDefault();
          useGeometryStore.temporal.getState().undo();
          return;
        }
        if ((k === 'z' && e.shiftKey) || k === 'y') {
          e.preventDefault();
          useGeometryStore.temporal.getState().redo();
          return;
        }
      }

      // Tool switch — ignore if modifier keys are held (avoid clash with browser combos)
      if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        const k = key.toLowerCase();
        if (TOOL_KEYS[k]) {
          e.preventDefault();
          setTool(TOOL_KEYS[k]);
          return;
        }
        // Tab — cycle wall kinds when the draw-wall tool is active.
        // Holding Shift inverts the cycle direction. We avoid stealing
        // Tab focus globally by only intercepting it for this specific
        // mode, so accessibility navigation keeps working everywhere else.
        if (key === 'Tab') {
          const st = useGeometryStore.getState();
          if (st.tool === TOOLS.DRAW_WALL) {
            e.preventDefault();
            const order = WALL_KIND_DEFS.map((d) => d.value);
            const idx = order.indexOf(st.drawWallKind);
            const next = e.shiftKey
              ? order[(idx - 1 + order.length) % order.length]
              : order[(idx + 1) % order.length];
            setDrawWallKind(next);
          }
          return;
        }
        if (key === 'Enter') {
          // Close the terrain polygon if one is being drawn.
          const st = useGeometryStore.getState();
          if (st.tool === TOOLS.DRAW_TERRAIN && st.terrainDraft.length >= 3) {
            e.preventDefault();
            commitTerrain();
          }
          return;
        }
        if (key === 'Escape') {
          e.preventDefault();
          setDrawingFrom(null);
          setChainStart(null);
          setCalibrationFrom(null);
          setStairsFrom(null);
          cancelTerrainDraft();
          clearSelection();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, setTool, setDrawingFrom, setChainStart, setCalibrationFrom, setStairsFrom,
      cancelTerrainDraft, commitTerrain, setDrawWallKind, clearSelection]);
};

export default useKeyboardShortcuts;
